import {
  type RoadmapFacetsResponse,
  type RoadmapNavigationResponse,
  RoadmapNodeRole,
  type RoadmapScopeRoot,
  type RoadmapTimeframe,
} from 'client';

export interface RoadmapNavigationScope {
  key: string;
  label: string;
  semanticRole: string | null;
  depth: number | null;
  presentationType: string | null;
  hierarchyAvailable: boolean;
  menuDepth: 0 | 1;
}

export interface RoadmapNavigationSelection {
  timeframe: RoadmapTimeframe | null;
  scopes: RoadmapNavigationScope[];
  scope: RoadmapNavigationScope | null;
}

const canonicalMatch = <T>(items: T[], requested: string | null, getKey: (item: T) => string) => {
  if (!requested) return null;
  const normalized = requested.trim().toLocaleLowerCase();
  return items.find((item) => getKey(item).toLocaleLowerCase() === normalized) ?? null;
};

const normalizeNavigationLabel = (value: string | null | undefined) =>
  (value ?? '').trim().replace(/\s+/g, ' ').toLocaleLowerCase();

const navigationLabelCollator = new Intl.Collator('en', {numeric: true, sensitivity: 'base'});

const sortNavigationChildren = (
  nodes: Array<NonNullable<RoadmapNavigationResponse['nodes']>[number]>
) =>
  [...nodes].sort(
    (left, right) =>
      navigationLabelCollator.compare(left.title, right.title) ||
      navigationLabelCollator.compare(left.key, right.key)
  );

const isPillarScope = (scope: RoadmapScopeRoot) =>
  scope.inferred_presentation_type === 'pillar' ||
  scope.companyos_reference?.kind === 'pillar_scope';

const isPlaceholderNode = (node: NonNullable<RoadmapNavigationResponse['nodes']>[number]) =>
  normalizeNavigationLabel(node.title) === 'n/a';

const isArchivedWorkstreamNode = (node: NonNullable<RoadmapNavigationResponse['nodes']>[number]) =>
  node.workstream_archived === true;

const hasWorkstreamReference = (node: NonNullable<RoadmapNavigationResponse['nodes']>[number]) =>
  node.workstream_id != null;

const isPillarScopeWrapperNode = (
  scope: RoadmapScopeRoot,
  node: NonNullable<RoadmapNavigationResponse['nodes']>[number],
  workstreamSiblingCount: number,
  childCount: number
) => {
  const title = normalizeNavigationLabel(node.title);
  const parentKey = node.parent_key ?? scope.root_node_key;
  const isSingleRootWorkstreamWithChildren =
    parentKey === scope.root_node_key &&
    hasWorkstreamReference(node) &&
    workstreamSiblingCount === 1 &&
    childCount > 0;
  return isSingleRootWorkstreamWithChildren || title === normalizeNavigationLabel(scope.label);
};

const scopesForTimeframe = (
  facets: RoadmapFacetsResponse,
  timeframe: RoadmapTimeframe | null
): RoadmapScopeRoot[] => {
  if (!timeframe) return facets.unassigned_scopes ?? [];
  return (
    facets.timeframe_scopes?.find((group) => group.timeframe_key === timeframe.key)?.scopes ?? []
  );
};

export const resolveRoadmapNavigation = (
  facets: RoadmapFacetsResponse,
  hierarchy: RoadmapNavigationResponse | null,
  requestedTimeframe: string | null,
  requestedScope: string | null
): RoadmapNavigationSelection => {
  const timeframes = facets.timeframes ?? [];
  const timeframe =
    canonicalMatch(timeframes, requestedTimeframe, (item) => item.key) ?? timeframes[0] ?? null;
  const hierarchyByKey = new Map((hierarchy?.nodes ?? []).map((node) => [node.key, node]));
  const childKeysByParent = new Map(
    (hierarchy?.child_groups ?? []).map((group) => [group.parent_key, group.child_keys ?? []])
  );
  const childGroupNodes = (parentKey: string) =>
    (childKeysByParent.get(parentKey) ?? [])
      .map((key) => hierarchyByKey.get(key))
      .filter(
        (child): child is NonNullable<typeof child> =>
          Boolean(child) && child?.semantic_role !== RoadmapNodeRole.ROADMAP_INITIATIVE
      );
  const pillarMenuChildren = (
    scope: RoadmapScopeRoot,
    node: NonNullable<RoadmapNavigationResponse['nodes']>[number],
    seenKeys: ReadonlySet<string>
  ): Array<NonNullable<RoadmapNavigationResponse['nodes']>[number]> => {
    if (seenKeys.has(node.key)) return [];
    const nextSeenKeys = new Set(seenKeys);
    nextSeenKeys.add(node.key);
    if (isArchivedWorkstreamNode(node)) return [];
    if (isPlaceholderNode(node)) return [];

    const siblings = childGroupNodes(node.parent_key ?? scope.root_node_key);
    const children = childGroupNodes(node.key);
    const workstreamSiblings = siblings.filter(hasWorkstreamReference);
    if (!isPillarScopeWrapperNode(scope, node, workstreamSiblings.length, children.length)) {
      return [node];
    }

    return children.flatMap((child) => pillarMenuChildren(scope, child, nextSeenKeys));
  };
  const scopes = scopesForTimeframe(facets, timeframe).flatMap((scope) => {
    const node = hierarchyByKey.get(scope.root_node_key);
    const root: RoadmapNavigationScope = {
      key: scope.root_node_key,
      label: scope.label,
      semanticRole: node?.semantic_role ?? null,
      depth: node?.depth ?? null,
      presentationType: scope.inferred_presentation_type ?? null,
      hierarchyAvailable: Boolean(node),
      menuDepth: 0,
    };
    const childNodes = childGroupNodes(scope.root_node_key);
    const visibleChildNodes = childNodes.filter((child) => !isArchivedWorkstreamNode(child));
    const menuChildNodes = isPillarScope(scope)
      ? visibleChildNodes.flatMap((child) =>
          pillarMenuChildren(scope, child, new Set([scope.root_node_key]))
        )
      : visibleChildNodes;
    const children: RoadmapNavigationScope[] = sortNavigationChildren(menuChildNodes).map(
      (child) => ({
        key: child.key,
        label: child.title,
        semanticRole: child.semantic_role,
        depth: child.depth,
        presentationType: null,
        hierarchyAvailable: true,
        menuDepth: 1,
      })
    );
    return [root, ...children];
  });
  const scope = canonicalMatch(scopes, requestedScope, (item) => item.key) ?? scopes[0] ?? null;

  return {timeframe, scopes, scope};
};
